/**
 * SfxPlayer — síntese procedural de efeitos sonoros via WebAudio.
 *
 * Por que procedural?
 *
 * - **Originalidade**: não dependemos de bibliotecas/arquivos externos
 *   (CLAUDE.md §9, §14). Todo som é construído com osciladores +
 *   envelopes ADSR simples — código autoral.
 * - **Peso**: zero bytes de áudio no bundle.
 * - **Variedade**: mesmo evento muda de pitch/forma com o contexto (combo,
 *   estrelas), evitando a sensação de loop curto repetitivo.
 *
 * Quando assets reais entrarem, basta plugar uma implementação alternativa
 * de SfxPlayer (Phaser.Sound) que respeite a mesma interface.
 */

export type SfxName =
  | 'correct'
  | 'correctTurbo'
  | 'wrong'
  | 'timeUp'
  | 'combo'
  | 'star'
  | 'levelComplete'
  | 'rankUp';

export interface SfxOptions {
  /** Combo atual (afeta pitch do som de combo/correct). */
  combo?: number;
  /** Índice da estrela (0..2) — usado pelo som 'star' para subir o tom. */
  starIndex?: number;
}

export interface SfxPlayer {
  play(name: SfxName, options?: SfxOptions): void;
  setVolume(volume01: number): void;
  /** Tenta destravar o AudioContext (necessário após gesto do usuário). */
  unlock(): Promise<void>;
}

/* ------------------------------------------------------------------ */
/* WebAudioSfxPlayer                                                   */
/* ------------------------------------------------------------------ */

type AudioContextCtor = typeof AudioContext;

export class WebAudioSfxPlayer implements SfxPlayer {
  private readonly ctx: AudioContext;
  private readonly masterGain: GainNode;

  constructor() {
    const Ctor: AudioContextCtor =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : ((globalThis as unknown as { webkitAudioContext: AudioContextCtor }).webkitAudioContext);
    this.ctx = new Ctor();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.8;
    this.masterGain.connect(this.ctx.destination);
  }

  setVolume(volume01: number): void {
    const clamped = Math.max(0, Math.min(1, volume01));
    this.masterGain.gain.setValueAtTime(clamped, this.ctx.currentTime);
  }

  async unlock(): Promise<void> {
    if (this.ctx.state === 'suspended') {
      try {
        await this.ctx.resume();
      } catch {
        // Browser bloqueou (sem gesto) — seguir, próxima tentativa pode dar certo.
      }
    }
  }

  play(name: SfxName, options: SfxOptions = {}): void {
    // Se o contexto está suspenso (ainda não houve gesto), tentamos
    // destravar de forma "best effort" — não bloqueia o som de quem já
    // teve gesto registrado.
    if (this.ctx.state === 'suspended') {
      void this.unlock();
    }

    switch (name) {
      case 'correct':
        this.ding(880, 0.22, 'sine');
        break;
      case 'correctTurbo':
        // Acerto rápido: dois semi-tons subindo, mais brilhante.
        this.ding(988, 0.18, 'sine');
        this.scheduleAfter(0.07, () => this.ding(1318, 0.16, 'sine'));
        break;
      case 'wrong':
        // Glissando descendente curto, sem dissonância — não punitivo.
        this.glide(440, 220, 0.32, 'sine', 0.18);
        break;
      case 'timeUp':
        // "Whoop" descendente um pouco mais longo.
        this.glide(380, 160, 0.55, 'triangle', 0.18);
        break;
      case 'combo': {
        const combo = options.combo ?? 5;
        // Arpeggio cresce conforme o combo: 5→tríade, 10→tétrade, 15+→pentatônica.
        const base = 523; // C5
        const ratios = combo >= 15 ? [1, 1.26, 1.5, 1.89, 2.25] : combo >= 10 ? [1, 1.26, 1.5, 1.89] : [1, 1.26, 1.5];
        ratios.forEach((r, i) => this.scheduleAfter(i * 0.07, () => this.ding(base * r, 0.18, 'square', 0.18)));
        break;
      }
      case 'star': {
        const idx = options.starIndex ?? 0;
        // Pings de estrela sobem em pitch a cada uma.
        this.ding(1047 + idx * 220, 0.18, 'triangle', 0.22);
        break;
      }
      case 'levelComplete': {
        // Mini-fanfarra: três notas + acorde final.
        const seq = [523, 659, 784];
        seq.forEach((f, i) => this.scheduleAfter(i * 0.12, () => this.ding(f, 0.22, 'square', 0.22)));
        this.scheduleAfter(0.42, () => {
          this.ding(1047, 0.4, 'square', 0.22);
          this.ding(1319, 0.4, 'sine', 0.18);
        });
        break;
      }
      case 'rankUp': {
        // Fanfarra mais épica: arpeggio + bell.
        const seq = [392, 523, 659, 784, 1047];
        seq.forEach((f, i) => this.scheduleAfter(i * 0.09, () => this.ding(f, 0.22, 'square', 0.24)));
        this.scheduleAfter(seq.length * 0.09 + 0.05, () => this.bell(1568, 0.7));
        break;
      }
    }
  }

  /* ------------------------------ helpers ------------------------------ */

  private ding(
    frequency: number,
    duration: number,
    type: OscillatorType = 'sine',
    peak: number = 0.3,
  ): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, now);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain).connect(this.masterGain);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  /** Glissando linear de `fromHz` para `toHz` em `duration` segundos. */
  private glide(
    fromHz: number,
    toHz: number,
    duration: number,
    type: OscillatorType,
    peak: number,
  ): void {
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(fromHz, now);
    osc.frequency.linearRampToValueAtTime(toHz, now + duration);
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    osc.connect(gain).connect(this.masterGain);
    osc.start(now);
    osc.stop(now + duration + 0.02);
  }

  /** Sino: senoide com decay longo + harmônico. */
  private bell(frequency: number, duration: number): void {
    const now = this.ctx.currentTime;
    [1, 2.01].forEach((mult, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency * mult;
      const peak = i === 0 ? 0.28 : 0.12;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(peak, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
      osc.connect(gain).connect(this.masterGain);
      osc.start(now);
      osc.stop(now + duration + 0.02);
    });
  }

  private scheduleAfter(seconds: number, fn: () => void): void {
    // setTimeout é "bom o suficiente" para SFX curtos (tolerância ~16ms).
    // Para timing musical preciso, usaríamos osc.start(when) — mas as
    // funções helpers já fazem isso internamente quando chamadas agora.
    setTimeout(fn, Math.max(0, seconds * 1000));
  }
}

/* ------------------------------------------------------------------ */
/* NoopSfxPlayer — usado quando WebAudio não está disponível ou nos    */
/* testes. play() é silencioso e não falha.                            */
/* ------------------------------------------------------------------ */
export class NoopSfxPlayer implements SfxPlayer {
  play(): void {}
  setVolume(): void {}
  async unlock(): Promise<void> {}
}

export function createDefaultSfxPlayer(): SfxPlayer {
  if (typeof AudioContext === 'undefined' && typeof (globalThis as { webkitAudioContext?: unknown }).webkitAudioContext === 'undefined') {
    return new NoopSfxPlayer();
  }
  try {
    return new WebAudioSfxPlayer();
  } catch {
    return new NoopSfxPlayer();
  }
}
